/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { union, or, filterBy } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import config from 'linced-app/config/environment';
import { pluralize } from '@linc-technologies/linced-common/utils/inflector';
import PaymentMethod from '@linc-technologies/linced-common/enums/payment-method';
import TransactionType from '@linc-technologies/linced-common/enums/transaction-type';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';
import moment from 'moment';
import { hash } from '@linc-technologies/linced-common/utils/promise';
import { loadPeople } from '@linc-technologies/linced-common/utils/load-people';

// tillUserIds gives the person ids of the till users in the till groups.
function* tillUserIds(...groups) {
  for (const group of groups) {
    if (!group) {
      continue;
    }
    for (const till of group.tills) {
      for (const user of till.users) {
        yield user.personId;
      }
    }
  }
}

export default class DailySummariesController extends Controller {
  // Services
  @service
  fetch;

  @service
  finance;

  @service
  roles;

  @service
  session;

  @service
  store;

  @service
  toaster;

  PaymentMethod = PaymentMethod;

  TransactionType = TransactionType;

  @tracked
  report = null;

  @tracked
  reportLoading = false;

  @tracked
  reportDialogOpen = false;

  @tracked
  reportSigned = false;

  @action
  setReportSigned(value) {
    this.reportSigned = value;
  }

  @tracked
  reportSaving = false;

  @tracked discrepancies = null;

  @tracked depositSlips = null;

  @tracked
  discrepanciesDialogOpen = false;

  @tracked
  discrepanciesSaving = false;

  @tracked
  discrepanciesAcknowledged = false;

  @action
  setDiscrepanciesAcknowledged(value) {
    this.discrepanciesAcknowledged = value;
  }

  @tracked
  generateDialogOpen = false;

  @tracked
  generateLoading = false;

  @tracked
  generateInProgress = false;

  @tracked
  generateWarnings = null;

  @tracked
  generateAnyway = false;

  @action
  setGenerateAnyway(value) {
    this.generateAnyway = value;
  }

  @tracked
  feesReportSigned = false;

  @action
  setFeesReportSigned(value) {
    this.feesReportSigned = value;
  }

  fessReportSaving = false;

  @tracked
  refundsReportSigned = false;

  @action
  setRefundsReportSigned(value) {
    this.refundsReportSigned = value;
  }

  refundsReportSaving = false;

  @tracked cancelledFees = null;

  @tracked adjustedFees = null;

  @union('cancelledFees', 'adjustedFees')
  feesReportData;

  @computed('feesReportData.@each.displayAmount')
  get feesReportTotal() {
    return this.feesReportData.reduce(
      (total, row) => total + row.displayAmount,
      0,
    );
  }

  @or('cancelledFees.length', 'adjustedFees.length')
  feesReport;

  @tracked
  feesReportOpen = false;

  @tracked cancelledReceipts = null;

  @tracked refunds = null;

  @union('cancelledReceipts', 'refunds')
  refundsReportData;

  @computed('refundsReportData.@each.amount')
  get refundsReportTotal() {
    return this.refundsReportData.reduce((total, row) => total + row.amount, 0);
  }

  @or('cancelledReceipts.length', 'refunds.length')
  refundsReport;

  @tracked
  refundsReportOpen = false;

  // canSign determines whether the current user can sign daily summaries.
  @or('roles.isLincStaff', 'roles.isPrincipalOrDelegate')
  canSign;

  @computed('finance.availablePaymentMethods.[]')
  get physicalPaymentMethods() {
    return this.finance.availablePaymentMethods.filter((method) => {
      return [
        PaymentMethod.CASH,
        PaymentMethod.CHEQUE,
        PaymentMethod.MONEY_ORDER,
      ].includes(method.value);
    });
  }

  @computed('finance.availablePaymentMethods.[]')
  get bankedPaymentMethods() {
    let methods = [];
    methods.push(...this.finance.availablePaymentMethods);
    methods.push({ value: PaymentMethod.ONLINE, label: 'Online' });
    return methods;
  }

  @filterBy('report.banked.tills', 'tillCode')
  bankedTills;

  @filterBy('report.inSafe.tills', 'tillCode')
  unbankedTills;

  @computed('discrepancies.@each.{method,amount}')
  get discrepanciesByMethod() {
    if (isEmpty(this.discrepancies)) {
      return {};
    }
    return this.discrepancies.reduce((mapped, discrepancy) => {
      mapped[discrepancy.method] += discrepancy.amount;
      return mapped;
    }, {});
  }

  @computed('discrepancies.@each.isAcknowledged')
  get allDiscrepanciesAcknowledged() {
    if (isEmpty(this.discrepancies)) {
      return true;
    }
    return this.discrepancies.every(
      (discrepancy) => discrepancy.isAcknowledged,
    );
  }

  @computed('discrepancies.[]')
  get firstDiscrepancy() {
    if (isEmpty(this.discrepancies)) {
      return null;
    }
    return this.discrepancies.at(0);
  }

  // transactionTotals provides a map of payment method to transaction total.
  @computed('report.banked.totals.@each.{method,amount}')
  get transactionTotals() {
    let byMethod = {
      [PaymentMethod.CASH]: 0,
      [PaymentMethod.CHEQUE]: 0,
      [PaymentMethod.MONEY_ORDER]: 0,
    };
    let totals = this.get('report.banked.totals');
    if (isEmpty(totals)) {
      return byMethod;
    }
    return totals.reduce((mapped, total) => {
      mapped[total.method] = total.amount;
      return mapped;
    }, byMethod);
  }

  // cashTransactionsTotal calculates the total amount of cash transactions.
  @computed('transactionTotals')
  get cashTransactionsTotal() {
    return this.get(`transactionTotals.${PaymentMethod.CASH}`);
  }

  // chequeTransactionsTotal calculates the total amount of cheque
  // transactions.
  @computed('transactionTotals')
  get chequeTransactionsTotal() {
    return this.get(`transactionTotals.${PaymentMethod.CHEQUE}`);
  }

  // moneyOrderTransactionsTotal calculates the total amount of Money Order
  // transactions.
  @computed('transactionTotals')
  get moneyOrderTransactionsTotal() {
    return this.get(`transactionTotals.${PaymentMethod.MONEY_ORDER}`);
  }

  // cashBankedTotal calculates the total amount of cash banked.
  @computed('depositSlips.@each.totalAmount')
  get cashBankedTotal() {
    if (isEmpty(this.depositSlips)) {
      return 0;
    }
    return this.depositSlips.reduce((total, slip) => {
      return total + slip.totalAmount;
    }, 0);
  }

  // cashVariance calculates any variance between cash receipted and banked.
  @computed('cashTransactionsTotal', 'cashBankedTotal')
  get cashVariance() {
    return this.cashBankedTotal - this.cashTransactionsTotal;
  }

  // chequeBankedTotal calculates the total amount of cheques banked.
  @computed(
    'chequeTransactionsTotal',
    'depositSlips.@each.chequeDiscrepanciesTotal',
  )
  get chequeBankedTotal() {
    let discrepanciesTotal = 0;
    if (!isEmpty(this.depositSlips)) {
      discrepanciesTotal = this.depositSlips.reduce((total, slip) => {
        return total + slip.chequeDiscrepanciesTotal;
      }, 0);
    }
    return this.chequeTransactionsTotal - discrepanciesTotal;
  }

  // chequeVariance calculates any variance between cheques receipted and banked.
  @computed('chequeTransactionsTotal', 'chequeBankedTotal')
  get chequeVariance() {
    return this.chequeBankedTotal - this.chequeTransactionsTotal;
  }

  // moneyOrderBankedTotal calculates the total amount of Money Orders banked.
  @computed(
    'moneyOrderTransactionsTotal',
    'depositSlips.@each.moneyOrderDiscrepanciesTotal',
  )
  get moneyOrderBankedTotal() {
    let discrepanciesTotal = 0;
    if (!isEmpty(this.depositSlips)) {
      discrepanciesTotal = this.depositSlips.reduce((total, slip) => {
        return total + slip.moneyOrderDiscrepanciesTotal;
      }, 0);
    }
    return this.moneyOrderTransactionsTotal - discrepanciesTotal;
  }

  // moneyOrderVariance calculates any variance between money orders receipted
  // and banked.
  @computed('moneyOrderTransactionsTotal', 'moneyOrderBankedTotal')
  get moneyOrderVariance() {
    return this.moneyOrderBankedTotal - this.moneyOrderTransactionsTotal;
  }

  @computed('report.methodTotals.@each.amount')
  get reportTotal() {
    if (isEmpty(this.report.methodTotals)) {
      return 0;
    }
    return this.report.methodTotals.reduce((total, { method, amount }) => {
      if (!findBy(this.bankedPaymentMethods, 'value', method)) {
        // Don't include any methods which aren't shown in the "BANK" table.
        // EFT Refunds fall into this category, and shouldn't be counted.
        return total;
      }
      return total + amount;
    }, 0);
  }

  @computed('cashBankedTotal', 'chequeBankedTotal', 'moneyOrderBankedTotal')
  get bankedTotal() {
    return (
      this.cashBankedTotal + this.chequeBankedTotal + this.moneyOrderBankedTotal
    );
  }

  @computed('cashVariance', 'chequeVariance', 'moneyOrderVariance')
  get varianceTotal() {
    return this.cashVariance + this.chequeVariance + this.moneyOrderVariance;
  }

  @computed('discrepancies.@each.amount')
  get discrepanciesTotal() {
    if (isEmpty(this.discrepancies)) {
      return 0;
    }
    return this.discrepancies.reduce((total, { amount }) => {
      return total + amount;
    }, 0);
  }

  @action
  openGenerateDialog() {
    this.generateInProgress = false;
    this.generateLoading = true;
    this.generateDialogOpen = true;
    this.generateWarnings = [];
    this.generateAnyway = false;

    // Get potentially warning-worthy till sessions.
    hash({
      open: this.store.query('finance/till-session', { isOpen: true }),
      unbanked: this.store.query('finance/till-session', {
        isClosed: true,
        isUnbanked: true,
        isTillBased: true,
      }),
    }).then((sessions) => {
      // Determine whether there are any warnings to show.
      let warnings = [];
      if (!isEmpty(sessions.open)) {
        warnings.push(
          pluralize(sessions.open.length, 'open till session') +
            ' will not be included',
        );
      }
      if (!isEmpty(sessions.unbanked)) {
        let totalUnbanked = sessions.unbanked.reduce((total, tillSession) => {
          return total + tillSession.balance;
        }, 0);
        if (totalUnbanked >= 500) {
          warnings.push('Cash receipts exceeding $500 have not been banked');
        }
        let aWeekAgo = moment().subtract(7, 'days');
        let oldUnbanked = sessions.unbanked.filter((tillSession) => {
          return moment.unix(tillSession.createTime).isBefore(aWeekAgo, 'day');
        });
        if (!isEmpty(oldUnbanked)) {
          warnings.push(
            pluralize(oldUnbanked.length, 'till session') +
              ' unbanked for a week or more',
          );
        }
      }
      this.generateWarnings = warnings;
      if (isEmpty(this.generateWarnings)) {
        // No warnigns - just go ahead and generate the summary.
        this.generateDailySummary();
      } else {
        // Warnings - show them (don't generate a summary yet).
        this.generateLoading = false;
      }
    });
  }

  @action
  closeGenerateDialog() {
    this.generateDialogOpen = false;
  }

  @action
  signReport() {
    this.reportSaving = true;

    let url = `${config.APP.apiHost}/finance/${config.APP.apiPath}/dailysummaries/${this.report.id}/:sign`;
    let data = {
      schoolId: this.session.activeSchool,
    };
    return this.fetch
      .post(url, { data })
      .then(() => this.report.reload())
      .then(() => {
        this.report = null;
        this.reportDialogOpen = false;
      })
      .catch(() => {
        this.toaster.error(`Error signing Daily Summary`);
      })
      .finally(() => {
        this.reportSaving = false;
      });
  }

  @action
  viewDiscrepancies() {
    this.discrepanciesDialogOpen = true;
  }

  @action
  closeDiscrepanciesDialog() {
    this.discrepanciesDialogOpen = false;
  }

  @action
  acknowledgeDiscrepancies() {
    this.discrepanciesSaving = true;
    let discrepancyPromises = this.discrepancies.reduce(
      (promises, discrepancy) => {
        let url = `${config.APP.apiHost}/finance/${config.APP.apiPath}/discrepancies/${discrepancy.id}/:acknowledge`;
        let data = {
          schoolId: this.session.activeSchool,
        };
        promises.push(this.fetch.post(url, { data }));
        return promises;
      },
      [],
    );
    Promise.all(discrepancyPromises)
      .then(() => {
        return this.discrepancies.update();
      })
      .then(() => {
        this.discrepanciesDialogOpen = false;
      })
      .catch(() => {
        this.toaster.error(`Error acknowledging discrepancy`);
      })
      .finally(() => {
        this.discrepanciesSaving = false;
      });
  }

  @action
  viewFeesReport() {
    this.feesReportOpen = true;
  }

  @action
  viewRefundsReport() {
    this.refundsReportOpen = true;
  }

  @action
  signSubreport(subreport) {
    this.reportSaving = true;

    let url = `${config.APP.apiHost}/finance/${config.APP.apiPath}/dailysummaries/${this.report.id}/:signSubreport`;
    let data = {
      schoolId: this.session.activeSchool,
      subreport,
    };
    return this.fetch
      .post(url, { data })
      .then(() => this.report.reload())
      .then(() => {
        this.setProperties({
          [`${subreport}ReportOpen`]: false,
          [`${subreport}ReportSigned`]: false,
        });
      })
      .catch(() => {
        this.toaster.error(`Error signing report`);
      })
      .finally(() => {
        this.reportSaving = false;
      });
  }

  @action
  closeFeesReport() {
    this.feesReportOpen = false;
    this.feesReportSigned = false;
  }

  @action
  closeRefundsReport() {
    this.refundsReportOpen = false;
    this.refundsReportSigned = false;
  }

  @action
  closeReport() {
    this.report = null;
    this.reportLoading = false;
    this.reportDialogOpen = false;
    this.reportSaving = false;
    this.reportSigned = false;
    this.discrepanciesAcknowledged = false;
  }

  @action
  viewReport(report, skipRefresh = false) {
    this.report = report;
    this.reportLoading = true;
    this.reportDialogOpen = true;
    this.reportSigned = false;
    this.discrepanciesAcknowledged = false;
    this.depositSlips = null;
    this.discrepancies = null;
    this.generateDialogOpen = false;
    this.cancelledFees = null;
    this.adjustedFees = null;
    this.cancelledReceipts = null;
    this.refunds = null;
    let promises = {
      depositSlips: this.store.query('finance/deposit-slip', {
        dailySummaryId: report.id,
      }),
      discrepancies: this.store.query('finance/discrepancy', {
        dailySummaryId: report.id,
      }),
      cancelledFees: hash({
        voids: this.store.query('finance/transaction', {
          type: TransactionType.VOID,
          dailySummaryId: report.id,
        }),
        writeoffs: this.store.query('finance/transaction', {
          type: TransactionType.WRITEOFF,
          dailySummaryId: report.id,
        }),
      }).then((data) => {
        return [...data.voids, ...data.writeoffs];
      }),
      adjustedFees: this.store.query('finance/transaction', {
        type: TransactionType.DISCOUNT___EXEMPTION,
        dailySummaryId: report.id,
      }),
      cancelledReceipts: this.store.query('finance/transaction', {
        isCancelled: true,
        cancellationDailySummaryId: report.id,
      }),
      refunds: this.store.query('finance/transaction', {
        type: TransactionType.REFUND,
        dailySummaryId: report.id,
      }),
    };
    if (!skipRefresh) {
      // Reload in order to calculate the actual report content.
      promises.report = report.reload();
    }
    hash(promises).then(async (data) => {
      // The report shows the till users' names.
      await loadPeople(this.store, tillUserIds(report.banked, report.inSafe));
      data.reportLoading = false;
      this.setProperties(data);
    });
  }

  @action
  generateDailySummary() {
    this.generateInProgress = true;
    let url = `${config.APP.apiHost}/finance/${config.APP.apiPath}/dailysummaries/:generate`;
    let data = {
      schoolId: this.session.activeSchool,
    };
    return this.fetch
      .post(url, { data })
      .then((resp) => {
        // Push the generated daily summary into the store, then show it.
        let dailySummary = this.store.push(
          this.store.normalize('finance/daily-summary', resp.dailySummary),
        );
        this.viewReport(dailySummary, true);
      })
      .catch(() => {
        this.toaster.error(`Error generating daily summary`);
      })
      .finally(() => {
        this.generateInProgress = false;
      });
  }
}
